import React from "react";
import { Typography, Row, Col, Card, Tag, Progress } from "antd";
import { FileSearchOutlined, UserOutlined } from "@ant-design/icons";
import { ParsedResumeContent } from "../../Interface/Index";
import { Requirements } from "../../Entities/job-description.entity";
import "./ProfileAnalysisView.scss";
import { formatDate } from "../../Utils/date.utils";

interface ProfileAnalysisViewProps {
  jobRequirements: Requirements[];
  resumeData: ParsedResumeContent | null;
  jobTitle?: string;
  isAnalyzing: boolean;
  analysisMessage?: string;
}

const ProfileAnalysisView: React.FC<ProfileAnalysisViewProps> = ({
  jobRequirements,
  resumeData,
  jobTitle,
  isAnalyzing,
  analysisMessage,
}) => {
  const candidateName =
    [resumeData?.basicDetails?.firstName, resumeData?.basicDetails?.lastName]
      .filter(Boolean)
      .join(" ") || "Candidate";

  return (
    <div className="profile-analysis-view">
      {/* Simple Header */}
      <div className="analysis-header">
        <Typography.Title level={5} style={{ margin: 0 }}>
          Profile Analysis
        </Typography.Title>
        <Typography.Text type="secondary">
          {analysisMessage ||
            "Analyzing your profile against job requirements..."}
        </Typography.Text>
      </div>

      {/* Main Content - Split View */}
      <div className="analysis-content">
        <Row gutter={24} className="analysis-columns">
          {/* Left Column - Job Requirements */}
          <Col xs={24} md={12} className="requirements-column">
            <Card
              className="analysis-card"
              title={
                <div className="card-header">
                  <FileSearchOutlined />
                  <span>Job Requirements</span>
                  {jobTitle && <Tag color="blue">{jobTitle}</Tag>}
                </div>
              }
              bordered={false}
            >
              <div className="requirements-list">
                {jobRequirements.map((req) => (
                  <div key={req.id} className="requirement-item">
                    <Typography.Text strong>{req.topic}</Typography.Text>
                    <Typography.Text
                      type="secondary"
                      className="requirement-desc"
                    >
                      {req.description}
                    </Typography.Text>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          {/* Right Column - Resume Summary */}
          <Col xs={24} md={12} className="resume-column">
            <Card
              className="analysis-card"
              title={
                <div className="card-header">
                  <UserOutlined />
                  <span>Your Profile</span>
                  <Tag color="green">{candidateName}</Tag>
                </div>
              }
              bordered={false}
            >
              <div className="resume-summary">
                {/* Professional Summary */}
                {resumeData?.summary && (
                  <div className="resume-section">
                    <Typography.Text strong className="section-label">
                      Professional Summary
                    </Typography.Text>
                    <Typography.Paragraph
                      ellipsis={{ rows: 3 }}
                      className="section-content"
                    >
                      {resumeData?.summary}
                    </Typography.Paragraph>
                  </div>
                )}

                {/* Skills */}
                {resumeData?.skills && resumeData?.skills.length > 0 && (
                  <div className="resume-section">
                    <Typography.Text strong className="section-label">
                      Skills
                    </Typography.Text>
                    <div className="skills-tags">
                      {resumeData?.skills.slice(0, 10).map((skill, index) => (
                        <Tag key={index} className="skill-tag">
                          {skill}
                        </Tag>
                      ))}
                      {resumeData?.skills.length > 10 && (
                        <Tag className="more-tag">
                          +{resumeData?.skills.length - 10} more
                        </Tag>
                      )}
                    </div>
                  </div>
                )}

                {/* Experience */}
                {resumeData?.experience && resumeData?.experience.length > 0 && (
                  <div className="resume-section">
                    <Typography.Text strong className="section-label">
                      Experience ({resumeData?.experience.length} positions)
                    </Typography.Text>
                    <div className="experience-list">
                      {resumeData.experience.slice(0, 3).map((exp, index) => (
                        <div key={index} className="experience-item">
                          <Typography.Text strong>
                            {exp.designation}
                          </Typography.Text>
                          {exp.companyName && (
                            <Typography.Text type="secondary">
                              {" at "}
                              {exp.companyName}
                            </Typography.Text>
                          )}
                          {exp.from && (
                            <Typography.Text
                              type="secondary"
                              className="period"
                            >
                              {exp.from
                                ? formatDate(exp.from.toString())
                                : "N/A"}{" "}
                              -{" "}
                              {exp.to
                                ? formatDate(exp.to.toString())
                                : "Present"}
                            </Typography.Text>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Education */}
                {resumeData?.education && resumeData?.education.length > 0 && (
                  <div className="resume-section">
                    <Typography.Text strong className="section-label">
                      Education
                    </Typography.Text>
                    <div className="education-list">
                      {resumeData?.education.slice(0, 2).map((edu, index) => (
                        <div key={index} className="education-item">
                          <Typography.Text>{edu.degree}</Typography.Text>
                          {edu.school && (
                            <Typography.Text type="secondary">
                              {" - "}
                              {edu.school}
                            </Typography.Text>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show message if no data */}
                {!resumeData?.summary &&
                  (!resumeData?.skills || resumeData?.skills.length === 0) &&
                  (!resumeData?.experience ||
                    resumeData?.experience.length === 0) &&
                  (!resumeData?.education ||
                    resumeData?.education.length === 0) && (
                    <Typography.Text type="secondary">
                      Resume content is being processed...
                    </Typography.Text>
                  )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Scanning Animation Overlay - Only scan beam */}
        {isAnalyzing && (
          <div className="scanning-overlay">
            <div className="scan-beam" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileAnalysisView;
